import React, { useMemo } from 'react';
import { CheckPicker, Input, SelectPicker } from 'rsuite';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useSurveyImageUrl } from '../../hooks/useSurveys';
import { questionNumbers, visibleBlockIds } from '../../lib/surveys';
import './SurveyForm.css';

/**
 * A survey as the person taking it sees it: text, links and images, then each question with its
 * answer control. Blocks with a condition only appear once the earlier answer calls for them.
 *
 * Controlled: `answers` is { [questionId]: value } and `onChange(nextAnswers)` gets every change.
 * `surveyId` lets uploaded images load. `readOnly` shows answers without letting them change.
 */
export default function SurveyForm({ surveyId, content, answers, onChange, readOnly = false, errors = {} }) {
  const blocks = useMemo(() => (Array.isArray(content) ? content : []), [content]);
  const visible = useMemo(() => visibleBlockIds(blocks, answers), [blocks, answers]);
  const numbers = useMemo(() => questionNumbers(blocks.filter((b) => visible.has(b.id))), [blocks, visible]);
  const setAnswer = (id, value) => onChange?.({ ...answers, [id]: value });

  return (
    <div className="svf">
      {blocks.map((block) => (
        <div key={block.id} className={`svf-block-wrap${visible.has(block.id) ? ' is-shown' : ''}`} aria-hidden={!visible.has(block.id)}>
          <div className="svf-block-wrap__inner" inert={!visible.has(block.id)}>
            <Block
              block={block}
              surveyId={surveyId}
              number={numbers.get(block.id)}
              value={answers?.[block.id]}
              onChange={(value) => setAnswer(block.id, value)}
              readOnly={readOnly}
              error={errors[block.id]}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function Block({ block, surveyId, number, value, onChange, readOnly, error }) {
  if (block.type === 'text') return <div className="svf-text">{block.text}</div>;
  if (block.type === 'link') {
    return (
      <a className="svf-link" href={block.url} target="_blank" rel="noopener noreferrer">
        <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
        {block.label || block.url}
      </a>
    );
  }
  if (block.type === 'image') return <SurveyImage block={block} surveyId={surveyId} />;

  const id = `svf-q-${block.id}`;
  return (
    <fieldset className={`svf-question${error ? ' has-error' : ''}`}>
      <legend className="svf-question__prompt" id={`${id}-label`}>
        {number ? <span className="svf-question__number">{number}.</span> : null}
        <span>{block.prompt || 'Untitled question'}</span>
        {block.required ? <span className="svf-question__required" aria-label="required">*</span> : null}
      </legend>
      {block.help ? <div className="svf-question__help">{block.help}</div> : null}
      <Answer block={block} id={id} value={value} onChange={onChange} readOnly={readOnly} />
      {error ? <div className="svf-question__error">{error}</div> : null}
    </fieldset>
  );
}

function Answer({ block, id, value, onChange, readOnly }) {
  const optionData = (block.options || []).map((o) => ({ value: o.id, label: o.label }));
  switch (block.kind) {
    case 'choice':
      return block.multiple ? (
        <CheckPicker
          block
          searchable={optionData.length > 8}
          data={optionData}
          value={Array.isArray(value) ? value : []}
          onChange={(v) => onChange(v || [])}
          placeholder="Choose any that apply"
          readOnly={readOnly}
          aria-labelledby={`${id}-label`}
        />
      ) : (
        <SelectPicker
          block
          searchable={optionData.length > 8}
          data={optionData}
          value={value ?? null}
          onChange={(v) => onChange(v ?? null)}
          placeholder="Choose an answer"
          readOnly={readOnly}
          aria-labelledby={`${id}-label`}
        />
      );
    case 'text':
      return (
        <Input
          as={block.multiline ? 'textarea' : 'input'}
          rows={block.multiline ? 4 : undefined}
          value={value ?? ''}
          onChange={(v) => onChange(v)}
          placeholder="Your answer"
          readOnly={readOnly}
          aria-labelledby={`${id}-label`}
        />
      );
    case 'rating':
      return <StarRating value={value} onChange={onChange} readOnly={readOnly} labelledBy={`${id}-label`} />;
    case 'ranking':
      return <Ranking options={block.options || []} value={value} onChange={onChange} readOnly={readOnly} />;
    case 'signature':
      return <Signature statement={block.statement} value={value} onChange={onChange} readOnly={readOnly} />;
    default:
      return null;
  }
}

function SurveyImage({ block, surveyId }) {
  const uploaded = useSurveyImageUrl(block.assetId ? surveyId : null, block.assetId);
  const src = block.assetId ? uploaded.url : block.url;
  return (
    <figure className="svf-image">
      {src ? <img src={src} alt={block.alt || ''} /> : <div className="svf-image__loading">Loading image…</div>}
      {block.caption ? <figcaption>{block.caption}</figcaption> : null}
    </figure>
  );
}

export function StarRating({ value, onChange, readOnly, labelledBy }) {
  const current = Number(value) || 0;
  return (
    <div className="svf-stars" role="radiogroup" aria-labelledby={labelledBy}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={current === n}
          aria-label={`${n} star${n === 1 ? '' : 's'}`}
          className={`svf-star${n <= current ? ' is-on' : ''}`}
          disabled={readOnly}
          onClick={() => onChange(current === n ? null : n)}
        >
          <i className={`bi ${n <= current ? 'bi-star-fill' : 'bi-star'}`} aria-hidden="true" />
        </button>
      ))}
      {current ? <span className="svf-stars__value">{current} of 5</span> : null}
    </div>
  );
}

function Ranking({ options, value, onChange, readOnly }) {
  // Until they move something, the order is the one the survey lists.
  const order = useMemo(() => {
    const ids = options.map((o) => o.id);
    const saved = Array.isArray(value) ? value.filter((id) => ids.includes(id)) : [];
    return [...saved, ...ids.filter((id) => !saved.includes(id))];
  }, [options, value]);
  const byId = new Map(options.map((o) => [o.id, o]));
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const move = (from, to) => {
    if (to < 0 || to >= order.length) return;
    onChange(arrayMove(order, from, to));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={({ active, over }) => {
        if (!over || active.id === over.id) return;
        move(order.indexOf(active.id), order.indexOf(over.id));
      }}
    >
      <SortableContext items={order} strategy={verticalListSortingStrategy}>
        <ol className="svf-rank">
          {order.map((id, index) => (
            <RankItem
              key={id}
              id={id}
              index={index}
              label={byId.get(id)?.label}
              last={index === order.length - 1}
              readOnly={readOnly}
              onMove={(delta) => move(index, index + delta)}
            />
          ))}
        </ol>
      </SortableContext>
      {!Array.isArray(value) && !readOnly ? (
        <div className="svf-question__help">Drag to put them in order, most important first.</div>
      ) : null}
    </DndContext>
  );
}

function RankItem({ id, index, label, last, readOnly, onMove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: readOnly });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`svf-rank__item${isDragging ? ' is-dragging' : ''}`}
    >
      {!readOnly ? (
        <button type="button" className="svf-rank__handle" aria-label={`Drag ${label}`} {...attributes} {...listeners}>
          <i className="bi bi-grip-vertical" aria-hidden="true" />
        </button>
      ) : null}
      <span className="svf-rank__position">{index + 1}</span>
      <span className="svf-rank__label">{label}</span>
      {!readOnly ? (
        <span className="svf-rank__moves">
          <button type="button" aria-label={`Move ${label} up`} disabled={index === 0} onClick={() => onMove(-1)}>
            <i className="bi bi-chevron-up" aria-hidden="true" />
          </button>
          <button type="button" aria-label={`Move ${label} down`} disabled={last} onClick={() => onMove(1)}>
            <i className="bi bi-chevron-down" aria-hidden="true" />
          </button>
        </span>
      ) : null}
    </li>
  );
}

function Signature({ statement, value, onChange, readOnly }) {
  const name = value?.name || '';
  return (
    <div className="svf-signature">
      {statement ? <div className="svf-signature__statement">{statement}</div> : null}
      <label className="svf-signature__line">
        <span className="svf-signature__label">Type your full name to sign</span>
        <Input
          value={name}
          onChange={(v) => onChange(v.trim() ? { name: v, signedAt: new Date().toISOString() } : null)}
          placeholder="Full name"
          readOnly={readOnly}
          className="svf-signature__input"
        />
      </label>
      {name.trim() ? (
        <div className="svf-signature__signed">
          <i className="bi bi-check-circle-fill" aria-hidden="true" /> Signed as {name.trim()}
        </div>
      ) : null}
    </div>
  );
}
